/**
 * Global Search API Types
 * Types for the /api/search endpoint that searches across Adventures, Users, and Groups
 */

// ============================================================================
// Request Types
// ============================================================================

export interface GlobalSearchRequest {
  query: string
  type?: SearchEntityType // Bitwise flags: 1=Adventure, 2=User, 4=Group, 8=Collections, 15=All
  tagIds?: string[] // Array of tag UUIDs for server-side filtering (TagType 1 & 3 allowed)
  latitude?: number
  longitude?: number
  radiusKm?: number
  skip?: number
  take?: number
  sortBy?: string
  sortDescending?: boolean
}

export enum SearchEntityType {
  All = 0,
  Adventure = 1,
  User = 2,
  Group = 4,
  Collections = 8,
}

export enum UserConnectionStatus {
  NotConnected = 0,
  Pending = 1,
  Connected = 2,
  Blocked = 3,
}

// ============================================================================
// Response Types
// ============================================================================

export interface GlobalSearchResponse {
  adventures: AdventureSearchResult[]
  users: UserSearchResult[]
  groups: GroupSearchResult[]
  collections: CollectionSearchResult[]
  totalResults: number
  searchQuery: string
}

// ============================================================================
// Adventure Search Result
// ============================================================================

export interface AdventureSearchResult {
  id: string
  title: string
  name: string
  description: string
  fullDescription: string
  quickDescription: string
  thumbnailUrl?: string | null
  status: number | string
  mainCategory: number
  startDate: string
  endDate: string
  completedDate?: string
  location: string
  latitude: number
  longitude: number
  approxLatitude?: number
  approxLongitude?: number
  distanceFeet?: number
  creatorId: string
  creatorName: string
  createdByUserName: string
  createdDate: string
  createdAt?: string
  updatedAt?: string
  participantCount: number
  isParticipant: boolean
  joinedDate?: string
  isRepeatable: boolean
  isPublic: boolean
  allowGroups: boolean
  maxParticipants: number
  difficultyLevel: number
  useDisplayForJoining: boolean
  tags: string[]
  // Rating scores
  averageRating: number
  ratingCount: number
  avgPresenceScore?: number
  avgObscuraScore?: number
  avgTrailcraftScore?: number
  avgCraftmarkScore?: number
  // Player activity stats
  uniquePlayersLastHour?: number
  uniquePlayersLast24Hours?: number
  uniquePlayersLastMonth?: number
  uniquePlayersLastYear?: number
  // Eligibility and criteria
  locationCount?: number
  isEligible?: boolean
  meetsDisplayCriteria?: boolean
  meetsJoinCriteria?: boolean
  joinCriteriaFailureReasons?: string[] | null
  requiresSubscription?: boolean
  hasTestVersion?: boolean
}

// ============================================================================
// User Search Result
// ============================================================================

export interface UserSearchResult {
  id: string
  userId: string
  displayName: string
  profilePicture?: string
  avatar?: string
  location?: string
  homeTown?: string
  bio?: string
  isConnected: boolean
  isOnline: boolean
  connectionStatus?: UserConnectionStatus
  status: number
  rank?: UserRankDto
}

export interface UserRankDto {
  tier: number
  name: string
  pointsRequired: number
  description: string
  badge?: string
  cssClass?: string
  themeFusion?: string
}

// ============================================================================
// Group Search Result
// ============================================================================

export interface GroupSearchResult {
  id: string
  name: string
  description: string
  isPublic: boolean // Deprecated - use visibility instead
  visibility?: number // GroupVisibility enum value
  maxMembers: number
  groupImage?: string
  creatorId: string
  creatorName: string
  createdDate: string
  memberCount: number
  isUserMember: boolean
  userRole: number
  isOwner: boolean
  members: GroupMemberDto[]
  interestTags: GroupInterestTagDto[]
  expirationDate?: string
  isArchived: boolean
  archivedDate?: string
  shareCode?: string
  shareCodeExpirationDate?: string
  shareCodeMaxUses?: number
  shareCodeUsedCount?: number
  // Pending request/invite status
  hasPendingJoinRequest?: boolean
  pendingJoinRequestId?: string | null
  hasPendingInvite?: boolean
  // Chat access
  canAccessChat?: boolean
}

export interface GroupMemberDto {
  groupId: string
  userId: string
  userName: string
  displayName: string
  userAvatar?: string
  hometown?: string
  reputation: number
  role: number
  joinedDate: string
}

export interface GroupInterestTagDto {
  id: string
  name: string
  displayName: string
  categoryName: string
  categoryDisplayName: string
}

// ============================================================================
// Collection Search Result
// ============================================================================

export interface CollectionSearchResult {
  id: string
  name: string
  shortDescription: string
  creatorName: string
  totalAdventureCount: number
  badgeCount: number
  isPublic: boolean
  createdDate: string
}

// ============================================================================
// Adventure Quick Stats Response
// ============================================================================

export interface AdventureQuickStatsResponse {
  uniquePlayersLastHour: number
  uniquePlayersLast24Hours: number
  uniquePlayersLastMonth: number
  uniquePlayersLastYear: number
  generatedAt: string
}
